import { Link, useNavigate } from 'react-router-dom'
import { Wand2, History, Sparkles, Volume2, User, LogOut, Settings, Shield, ArrowRight, Play, Star, Film, Palette } from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import { useTranslation } from '../i18n/LanguageContext'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useState, useRef, useEffect } from 'react'

export function LandingPage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()
  const { t } = useTranslation()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    await logout()
    setShowUserMenu(false)
    navigate('/')
  }


  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* 跳过链接 (键盘导航) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-cyan-500 text-white px-4 py-2 rounded z-50">
        {t.a11y.skipToContent}
      </a>

      {/* Header - 简洁现代设计 */}
      <header role="banner" className="sticky top-0 z-50 bg-[#161b22] border-b border-cyan-900/30">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" role="navigation" aria-label={t.a11y.mainNav}>
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white">KeyCore AI</span>
            </Link>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              {isAuthenticated && user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-400 hover:text-white transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:block text-sm font-medium">
                      {user.username || user.email.split('@')[0]}
                    </span>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-[#161b22] rounded-lg shadow-lg border border-cyan-900/30 py-1 z-30">
                      <Link to="/imagen" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <Wand2 className="w-4 h-4 mr-3" />
                        {t.userMenu.imageGen}
                      </Link>
                      <Link to="/tts" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <Volume2 className="w-4 h-4 mr-3" />
                        {t.userMenu.audioGen}
                      </Link>
                      <Link to="/history" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <History className="w-4 h-4 mr-3" />
                        {t.userMenu.history}
                      </Link>
                      <Link to="/director" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <Film className="w-4 h-4 mr-3" />
                        Director Studio
                      </Link>
                      <Link to="/promptcraft" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <Sparkles className="w-4 h-4 mr-3" />
                        Prompt Craft
                      </Link>
                      <Link to="/canvas" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <Palette className="w-4 h-4 mr-3" />
                        Aurora Canvas
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/admin" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                          <Shield className="w-4 h-4 mr-3" />
                          Admin Panel
                        </Link>
                      )}
                      <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-cyan-900/20" onClick={() => setShowUserMenu(false)}>
                        <Settings className="w-4 h-4 mr-3" />
                        {t.userMenu.settings}
                      </Link>
                      <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-900/20">
                        <LogOut className="w-4 h-4 mr-3" />
                        {t.nav.logout}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link to="/auth" className="text-gray-400 hover:text-white transition-colors">
                    {t.nav.login}
                  </Link>
                  <Link to="/auth" className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-colors font-medium">
                    {t.nav.register}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main id="main-content" role="main">
        {/* Hero Section - 简洁有力 */}
        <section className="relative py-20 sm:py-24 lg:py-32 overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[#0d1117]"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-900/30 text-cyan-400 text-sm font-medium mb-8">
                <Star className="w-4 h-4 mr-2" />
                Powered by AI
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
                Create Amazing
                <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  AI Content
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                Transform your ideas into stunning visuals and natural speech with our advanced AI platform.
                Fast, easy, and professional results.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Link
                  to="/imagen"
                  className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Wand2 className="w-5 h-5 mr-3" />
                  Start Creating Images
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/tts"
                  className="group inline-flex items-center px-8 py-4 bg-[#161b22] text-white rounded-xl font-semibold text-lg border border-cyan-900/30 hover:border-cyan-500/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Play className="w-5 h-5 mr-3" />
                  Try Voice Synthesis
                </Link>
              </div>

              {/* Social Proof */}
              <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
                <div className="flex items-center">
                  <div className="flex -space-x-2 mr-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full border-2 border-[#0d1117]"></div>
                    <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-green-600 rounded-full border-2 border-[#0d1117]"></div>
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full border-2 border-[#0d1117]"></div>
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full border-2 border-[#0d1117] flex items-center justify-center text-white text-xs font-bold">
                      +
                    </div>
                  </div>
                  <span>10,000+ creators</span>
                </div>
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-400 mr-1" />
                  <span>4.9/5 rating</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section - 简洁展示 */}
        <section className="py-20 bg-[#0d1117]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-white mb-4">Everything you need</h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Professional AI tools designed for creators, marketers, and businesses.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-[#161b22] rounded-2xl p-8 shadow-sm border border-cyan-900/30 hover:border-cyan-500/50 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-cyan-900/30 rounded-xl flex items-center justify-center mb-6">
                  <Wand2 className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">AI Image Generation</h3>
                <p className="text-gray-400 leading-relaxed">
                  Create stunning visuals from text descriptions. Powered by the latest Flux model for professional-quality results.
                </p>
              </div>

              <div className="bg-[#161b22] rounded-2xl p-8 shadow-sm border border-cyan-900/30 hover:border-cyan-500/50 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-900/30 rounded-xl flex items-center justify-center mb-6">
                  <Volume2 className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Voice Synthesis</h3>
                <p className="text-gray-400 leading-relaxed">
                  Convert text to natural, human-like speech. Supports multiple languages and voice styles for any content.
                </p>
              </div>

              <div className="bg-[#161b22] rounded-2xl p-8 shadow-sm border border-cyan-900/30 hover:border-cyan-500/50 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-purple-900/30 rounded-xl flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Smart Enhancement</h3>
                <p className="text-gray-400 leading-relaxed">
                  AI-powered prompts and optimizations to improve your creative output. Get better results faster.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - 简洁结尾 */}
        <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to create amazing AI content?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Join thousands of creators using KeyCore AI Studio
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center px-8 py-4 bg-white text-cyan-600 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors shadow-lg"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </section>
      </main>

      {/* Footer - 极简设计 */}
      <footer role="contentinfo" className="bg-[#161b22] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded"></div>
              <span className="font-semibold">KeyCore AI Studio</span>
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
              <a href="/contact" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="border-t border-cyan-900/30 mt-8 pt-8 text-center text-sm text-gray-400">
            © 2025 KeyCore AI Studio. Professional AI content creation platform.
          </div>
        </div>
      </footer>

      {/* 加载状态提示 */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {t.a11y.pageLoaded}
      </div>
    </div>
  )
}
